import React from 'react';
import _ from 'lodash';
import propTypes from 'prop-types';
import { ButtonWaiting } from 'client/components/Button/ButtonWaiting';
import BeRemoteErrorAlert from 'client/components/Be/BeRemoteErrorAlert';
import BeLoading from 'client/components/Be/BeLoading';
import NoticeRow from 'client/components/Helpers/NoticeRow';
import ObjectRow from 'client/components/Helpers/ObjectRow';

const ObjectGraph = ({topdeskOperator}) => {
  return (
    <>
      <ObjectRow subject="TOPdesk operator and related objects" object={topdeskOperator} />
    </>
  );
};

export default class EATDTesterOperator extends React.Component {
  static propTypes = {
    operator: propTypes.shape({
      id: propTypes.string.isRequired,
      status: propTypes.string,
      dynamicName: propTypes.string,
      isAnonymizing: propTypes.bool,
      isPreviewing: propTypes.bool,
      anonymizationError: propTypes.object,
      anonymizationResult: propTypes.object,
      previewError: propTypes.object,
      notices: propTypes.array,
      topdeskOperator: propTypes.object,
    }).isRequired,
    onRemove: propTypes.func.isRequired,
    onAnonymize: propTypes.func.isRequired,
    onPreviewAnonymization: propTypes.func.isRequired,
  };

  handleClickSync = ev => {
    if (!this.props.operator?.id) return;
    this.props.onSync(this.props.operator.id);
  };

  handleClickRemove = ev => {
    if (!this.props.operator?.id) return;
    this.props.onRemove(this.props.operator.id);
  };

  handleClickAnonymize = ev => {
    if (!this.props.operator?.id) return;
    this.props.onAnonymize(this.props.operator.id);
  };

  renderNoticeTableRows (notices) {
    if (!notices || !notices.length) {
      return (
        <tr>
          <td colSpan="3">
            <em>No action taken with this operator</em>
          </td>
        </tr>
      );
    }

    return notices.map((notice, index) => {
      return (
        <NoticeRow
          key={index}
          notice={notice}
        />
      );
    });
  }

  renderNoticeTable () {
    const { operator } = this.props;
    const { notices, topdeskOperator } = operator;
    if (!notices && !topdeskOperator) return null;
    return (
      <div className="list-group-item-text">
        <div className="row">
          <table className="table table-condensed table-striped xs-mb-0">
            <thead>
              <tr>
                <th width="15%">Action taken</th>
                <th width="60%">Description</th>
                <th width="25%"></th>
              </tr>
            </thead>
            <tbody>
              {topdeskOperator && <ObjectGraph topdeskOperator={topdeskOperator} />}
              {this.renderNoticeTableRows(notices)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render () {
    const { operator, onPreviewAnonymization } = this.props;
    if (!operator) return null;
    
    const { 
      id,
      status,
      dynamicName,
      isAnonymizing,
      anonymizationError,
      anonymizationResult, 
      previewError,
      error,
      isPreviewing,
      previewResult,
    } = operator;

    const topdeskOperatorId = id;
    const topdeskOperatorStatus = status;
    const operatorName = dynamicName || topdeskOperatorId;

    const hasSucceeded = !!anonymizationResult;
    const generalError = error || anonymizationError || previewError;

    return (
      <div className={`list-group-item xs-pb-0 ${hasSucceeded ? 'list-group-item-success' : ''}`}>
        <h4 className="list-group-item-heading xs-mb-15 xs-mt-0">
          <div className="name pull-left" style={{lineHeight: '30px'}}>
            <i className="icon mdi mdi-account xs-mr-5"></i>
            {operatorName} {topdeskOperatorStatus ? `(status: ${topdeskOperatorStatus})` : ''}
          </div>
          <span className="btn-group pull-right">
            <ButtonWaiting
              type="button"
              onClick={() => onPreviewAnonymization(topdeskOperatorId)}
              isWaiting={isPreviewing}
              disabled={isAnonymizing || isPreviewing || hasSucceeded}
              className="btn btn-rounded btn-info"
              title="Preview Anonymization Result"
            >
              <i className='icon icon-left mdi mdi-eye'></i>{' '}
              Preview
            </ButtonWaiting>
            <ButtonWaiting
              type="button"
              onClick={this.handleClickAnonymize}
              isWaiting={isAnonymizing}
              disabled={isAnonymizing || hasSucceeded}
              className={`btn btn-rounded ${hasSucceeded ? 'btn-success' : 'btn-warning'}`}
            >
              <i className={`icon icon-left mdi ${hasSucceeded ? 'mdi-check' : 'mdi-incognito'}`}></i>{' '}
              {hasSucceeded ? 'Anonymized' : 'Anonymize'}
            </ButtonWaiting>
            <button 
              onClick={this.handleClickRemove} 
              type="button" 
              className="btn btn-rounded btn-default"
              disabled={isAnonymizing || hasSucceeded}
            >
              <i className='icon icon-left mdi mdi-delete'></i>{' '}
              Remove
            </button>
          </span>
          <div className="clearfix"></div>
        </h4>
        <BeRemoteErrorAlert error={generalError} />
        {isPreviewing && (
          <BeLoading size={20}>
            {`Previewing anonymization for ${operatorName}...`}
          </BeLoading>
        )}
        {!isPreviewing && previewResult && (
          <div
            className='alert mt-2 p-2'
            style={{ backgroundColor: '#2b6cb0', color: '#ffffff', borderColor: '#1e4e8c' }}
          >
            <h6>Preview Results:</h6>
            {previewResult.notices && previewResult.notices.length > 0 && (
              <div className="mb-2">
                <p>Notices:</p>
                <ul>
                  {previewResult.notices.map((notice, idx) => (
                    <li key={idx}>{`${notice.action || ''} ${notice.object || ''} ${notice.id || ''}`.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
            {previewResult.data && (
              <div className="mb-2">
                <h6 className="mb-1">Field Changes:</h6>
                <table className='table table-sm table-bordered'>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Original Value</th>
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(previewResult.data.anonymized || {}).map(([field, newValue]) => (
                      <tr key={field}>
                        <td>{field}</td>
                        <td style={{ color: '#ffffff' }}>{String(_.get(previewResult.data.original, field) ?? '(empty)')}</td>
                        <td className='font-weight-bold' style={{ color: '#ffffff' }}>{String(newValue ?? '(empty)')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {Object.keys(previewResult.data.anonymized || {}).length === 0 && (
                  <div className='small'>No changes proposed by current rules/defaults.</div>
                )}
              </div>
            )}
          </div>
        )}
        
        {hasSucceeded && (
          <div className="alert alert-success mt-2 p-2">
            <strong>Anonymized successfully.</strong>
            {anonymizationResult.notices && anonymizationResult.notices.length > 0 && (
              <>
                <h6 className="mt-2">Details:</h6>
                <table className="table table-condensed table-striped xs-mb-0">
                  <tbody>
                    {this.renderNoticeTableRows(anonymizationResult.notices)}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}
